import { Link } from "react-router-dom";
import { ArrowRight, Compass } from "lucide-react";
import { Button } from "../components/ui/button";
import { testimonials, destinations, stats } from "../lib/mock";
import DestinationSlider from "../components/DestinationSlider";
import { Star, Leaf, ShieldCheck, HandCoins } from "lucide-react";

export default function Home() {
  return (
    <main className="paper-texture pt-16 sm:pt-20">
      {/* DESTINATION SLIDER (5 slides, world-map backdrop) */}
      <DestinationSlider />

      {/* INTRO */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 text-center">
        <div className="flex justify-center mb-4 sm:mb-6"><span className="leaf-divider">Who we are</span></div>
        <h2 className="font-serif text-2xl sm:text-3xl md:text-5xl font-bold text-[#1F4D3A] leading-tight mb-4 sm:mb-6">
          A travel company that measures success in <em className="text-[#C87A50] not-italic">stories</em>, not stars.
        </h2>
        <p className="text-base sm:text-lg text-[#4A3728] max-w-3xl mx-auto leading-relaxed">
          On Wheels was born in a battered Mahindra Thar on the road to Kaza. We are twelve guides, three storytellers and one belief — that the finest itineraries are written between the milestones, not on them.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 mt-10 sm:mt-16">
          {[
            { icon: Leaf, title: 'Low-impact', desc: 'Homestays over hotels. Local guides over algorithms. Groups of 6–16.' },
            { icon: ShieldCheck, title: 'Safety first', desc: 'Certified drivers, oxygen on high passes, and a captain per group of ten.' },
            { icon: HandCoins, title: 'Honest pricing', desc: 'All-inclusive quotes. No hidden add-ons. Ever.' }
          ].map((f) => (
            <div key={f.title} className="bg-white rounded-2xl p-6 sm:p-8 border border-[#E5DDCB] card-lift">
              <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-[#F5F0E8] flex items-center justify-center mb-4 sm:mb-5 mx-auto">
                <f.icon className="w-5 h-5 sm:w-6 sm:h-6 text-[#1F4D3A]" strokeWidth={2} />
              </div>
              <h3 className="font-serif text-lg sm:text-xl font-semibold text-[#1F4D3A] mb-2">{f.title}</h3>
              <p className="text-sm text-[#5A4A38] leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURED TOURS */}
      {/* DESTINATIONS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
        <div className="text-center mb-10 sm:mb-14">
          <div className="flex justify-center mb-3 sm:mb-4"><span className="leaf-divider">Where we go</span></div>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold text-[#1F4D3A] leading-tight">Six regions, endless stories.</h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
          {destinations.map((d) => (
            <Link to="/tours" key={d.name} className="relative aspect-[3/4] rounded-xl sm:rounded-2xl overflow-hidden group img-zoom">
              <img src={d.image} alt={d.name} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1F4D3A]/85 via-transparent to-transparent" />
              <div className="absolute bottom-3 left-3 right-3 sm:bottom-4 sm:left-4 sm:right-4 text-white">
                <h3 className="font-serif text-base sm:text-xl font-bold">{d.name}</h3>
                <p className="text-[10px] sm:text-xs text-[#D9CDB0] mt-0.5 sm:mt-1">{d.trips} journeys</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-[#1F4D3A] text-[#F5F0E8] py-16 sm:py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 sm:mb-16">
            <span className="leaf-divider inline-flex text-[#C9A961]">How it works</span>
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold text-white mt-3 sm:mt-4">Three steps to the road.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 sm:gap-10">
            {[
              { n: '01', t: 'Pick your route', d: 'Browse curated journeys or tell us your dream region — we\'ll shape an itinerary around it.' },
              { n: '02', t: 'Confirm your seat', d: 'A modest deposit locks your date. We handle permits, stays, transport and the coffee stops.' },
              { n: '03', t: 'Just show up', d: 'Meet your captain and travel companions at the start point. The road takes over from there.' }
            ].map((s) => (
              <div key={s.n} className="relative">
                <div className="font-serif text-5xl sm:text-6xl md:text-7xl font-bold text-[#C9A961]/30 leading-none">{s.n}</div>
                <h3 className="font-serif text-xl sm:text-2xl font-semibold text-white mt-2 mb-2 sm:mb-3">{s.t}</h3>
                <p className="text-sm text-[#D9CDB0] leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
        <div className="text-center mb-10 sm:mb-14">
          <div className="flex justify-center mb-3 sm:mb-4"><span className="leaf-divider">Travellers say</span></div>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold text-[#1F4D3A] leading-tight">Words from the road.</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
          {testimonials.map((t) => (
            <div key={t.name} className="bg-white rounded-2xl p-6 sm:p-8 border border-[#E5DDCB] card-lift">
              <div className="flex gap-1 mb-3 sm:mb-4">
                {Array.from({length: t.rating}).map((_, i) => (
                  <Star key={`${t.name}-star-${i}`} className="w-4 h-4 fill-[#C9A961] text-[#C9A961]" />
                ))}
              </div>
              <p className="text-[#4A3728] leading-relaxed mb-5 sm:mb-6 font-serif text-base sm:text-lg italic">"{t.text}"</p>
              <div className="flex items-center gap-3 sm:gap-4 pt-3 sm:pt-4 border-t border-[#E5DDCB]">
                <img src={t.avatar} alt={t.name} className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-[#F5F0E8]" />
                <div>
                  <div className="font-semibold text-[#1F4D3A] text-sm sm:text-base">{t.name}</div>
                  <div className="text-[11px] sm:text-xs text-[#6B8E5A]">{t.location} · {t.tour}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 sm:pb-24">
        <div className="relative rounded-2xl sm:rounded-3xl overflow-hidden p-8 sm:p-12 md:p-20">
          <img
            src="https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHxyb2FkJTIwdHJpcHxlbnwwfHx8fDE3ODkyOTQ0NzB8MA&ixlib=rb-4.1.0&q=85"
            className="absolute inset-0 w-full h-full object-cover"
            alt="open road"
          />
          <div className="absolute inset-0 bg-[#1F4D3A]/75" />
          <div className="relative z-10 text-center text-white max-w-3xl mx-auto">
            <Compass className="w-8 h-8 sm:w-10 sm:h-10 text-[#C9A961] mx-auto mb-3 sm:mb-4" />
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold mb-4 sm:mb-6 leading-tight">Ready to hit the road?</h2>
            <p className="text-base sm:text-lg text-[#E5DDCB] mb-6 sm:mb-8">Tell us where you dream of and we'll design a journey to match — fixed departures or a private trip for your tribe.</p>
            <Link to="/contact">
              <Button className="btn-primary bg-[#C87A50] hover:bg-[#B4653A] text-white rounded-full px-6 sm:px-10 h-12 sm:h-14 text-sm sm:text-base">
                Plan my trip <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
